<template>
  <InfoCard
    class="base-info-container"
    icon="iconfont icona-jobname"
    :title="$t(`${i18nPrefix}.title.${ isPartTime ? 'partTimeExperience' : 'workExperience' }`)"
    :infoList="infoList"
    :infoLines="infoLines"
  >
    <template #fitScore>
      <div v-if="showFitScore" class="score-container">
        <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="$t(`${i18nPrefix}.label.workExperienceFit`)">
          <div ref="fitScore" class="chart-score"></div>
        </el-tooltip>
        <el-tooltip placement="top-start" effect="light" popper-class="light-tooltip-popper" :content="$t(`${i18nPrefix}.tip.resumeFitInfo`)">
          <i class="tip-icon iconfont iconbianzu1"></i>
        </el-tooltip>
      </div>
    </template>
  </InfoCard>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import moment from 'moment'
import InfoCard from '@/components/candidate/infoCard.vue'
import { addI18nProperty, getI18nPropertyValue } from '@/i18n'
import { generateFitEchartsOption } from '@/constant/application'
import { formatBigNumber } from '@/components/filters'

export default {
  components: {
    InfoCard
  },
  props: {
    dataList: {
      type: Array,
      default: () => []
    },
    isPartTime: {
      type: Boolean,
      default: false
    },
    fitScore: {
      require: false
    },
    showFitScore: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate'
    }
  },
  computed: {
    infoLines () {
      const i18nPrefix = this.i18nPrefix
      const lines = {
        company_name: {
          labelI18nKey: `${i18nPrefix}.label.companyName`
        },
        job_title: {
          labelI18nKey: `${i18nPrefix}.label.jobTitle`
        },
        industry: {
          labelI18nKey: `${i18nPrefix}.label.industry`,
          formatter: (val, info) => info.industry?.i18nName
        },
        location: {
          labelI18nKey: `${i18nPrefix}.label.location`,
          formatter: this.formatLocation
        },
        date: {
          labelI18nKey: `${i18nPrefix}.label.date`,
          formatter: this.formatWorkTime,
          multipleText: true
        },
        salary: {
          labelI18nKey: `${i18nPrefix}.label.salary`,
          formatter: (val, info) => {
            const currency = info.currency && info.currency !== '---' ? info.currency : ''
            return `${formatBigNumber(info.salary?.i18nName) || ''} ${currency}`
          }
        },
        summary: {
          labelI18nKey: `${i18nPrefix}.label.description`,
          multipleText: true,
          fullLine: true
        }
      }
      return lines
    },
    infoList () {
      return _.map(this.dataList, item => {
        addI18nProperty(item.salary, 'i18nName', 'cn_name', 'name')
        addI18nProperty(item.industry, 'i18nName', 'cn_name', 'name')
        return item
      })
    }
  },
  created () {

  },
  mounted () {
  },
  methods: {
    formatLocation (location, info) {
      if (!location) {
        return
      }
      return _.chain(['country', 'province', 'city']).map(key => {
        const val = _.get(location, key)
        if (!val) {
          return
        }
        return getI18nPropertyValue(val, 'cn_name', 'name')
      }).filter().reverse().join(' ').value()
    },
    getWorkDuration (startMoment, endMoment) {
      const totalMonths = endMoment.clone().startOf('month').diff(startMoment.clone().startOf('month'), 'months')
      if (totalMonths <= 0) {
        return {}
      }
      return {
        years: Math.floor(totalMonths / 12),
        months: totalMonths % 12
      }
    },
    formatWorkTime (val, info) {
      const startDate = info.start_date
      const endDate = info.end_date
      if (!startDate) {
        return
      }
      const startMoment = moment(startDate)
      const endMoment = endDate && moment(endDate)
      const startDateStr = startMoment.format('YYYY.MM')
      const endDateStr = endDate ? endMoment.format('YYYY.MM') : this.$t(`${this.i18nPrefix}.label.now`)
      const workTime = `${startDateStr} - ${endDateStr}`
      if (endDate) {
        const duration = this.getWorkDuration(startMoment, endMoment)
        const durationStr = _.chain(['years', 'months']).map(key => {
          let dateI18nKey = key
          const dateNum = duration[key]
          if (!dateNum) {
            return
          }
          if (dateNum === 1) {
            dateI18nKey = key.replace('s', '')
          }
          return this.$t(`${this.i18nPrefix}.label.dates.${dateI18nKey}`, { num: dateNum })
        }).filter().join(' & ').trim().value()
        if (durationStr) {
          return `${workTime}\n(${durationStr})`
        }
      }
      return workTime
    },
    initExperienceScore () {
      if (!this.showFitScore) {
        return
      }
      const el = this.$refs.fitScore
      const val = this.fitScore * 10
      let color = 'rgb(226, 118, 118)' // red
      if (val > 40 && val < 70) {
        color = 'rgb(244, 169, 88)' // orange
      } else if (val >= 70) {
        color = 'rgb(81, 191, 130)' // green
      }
      const option = generateFitEchartsOption(val, val, color, 18)
      const series = option.series
      series.axisLine.lineStyle.width = 4
      const chart = echarts.init(el)
      chart.setOption(option)
    }
  }
}
</script>

<style lang="scss" scoped>
.base-info-container {
  .detail-item {
    display: inline-block;
    vertical-align: top;
    width: 50%;
    margin-top: 16px;

    &:nth-child(1), &:nth-child(2) {
      margin-top: 0;
    }

    .label {
      font-weight: bold;
      line-height: 20px;
    }

    .content {
      color: #606266;
    }
  }

  .description {
    width: 100%;
  }

  .score-container {
    position: relative;
    padding-right: 20px;
    .chart-score {
      display: inline-block;
      vertical-align: top;
      width: 45px;
      height: 45px;
    }

    .tip-icon {
      font-size: 14px;
      color: #c0c4cc;
      cursor: pointer;
      position: absolute;
      right: 0;
      top: -5px;
    }
  }
}
</style>
